/**
 * @author Fernando Ruano
 * @file saga.js
 * @description S3File Saga
 */

// Dependencies
import { put, call, takeLatest } from 'redux-saga/effects';

// Global actions
import { setResponse, setLoading } from '@nephro/redux/stores/app/actions';

// Services
import s3FileService from '@nephro/services/s3-file-service';

// Constants
import {
  GET_ALL_S3_FILES,
  GET_S3_FILE_BY_KEY,
  UPLOAD_S3_FILE,
  DELETE_S3_FILE_BY_KEY
} from './constants';

const customError = {
  type: 'Error',
  message: 'commons.problem'
};

/**
 * Call service to get S3 File from backend
 * @param { object } action - Action with payload data
 */
export function* getS3FilesArray(action) {
  yield put(setLoading(true));
  yield put(setResponse(false, false, false));
  const { callback } = action;
  try {
    const response = yield call(s3FileService.getS3FilesArray);
    yield put(setResponse('success', false, false, { ...response }, callback));
  } catch (err) {
    yield put(setResponse('error', false, false, false, callback));
    yield put(setResponse(false, false, false));
    yield put(setLoading(false));
  }
  yield put(setLoading(false));
}

/**
 * Call service to get S3 File info by key
 * @param { object } action - Action with payload data
 */
export function* getS3FileByKey(action) {
  yield put(setLoading(true));
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { key } = data;

  try {
    const response = yield call(s3FileService.getS3FileByKey, key);
    yield put(setResponse('success', false, false, { ...response }, callback));
  } catch (err) {
    yield put(setResponse('error', false, false, false, callback));
    yield put(setResponse(false, false, false));
    yield put(setLoading(false));
  }
}

/**
 * Call service to upload S3 File
 * @param { object } action - Action with payload data
 */
export function* uploadS3File(action) {
  yield put(setLoading(true));
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;

  try {
    const response = yield call(s3FileService.uploadS3File, data);
    yield put(setResponse('success', false, false, response, callback));
    yield put(setResponse(false, false, false));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
    yield put(setLoading(false));
  }
  yield put(setLoading(false));
}

/**
 * Call service to delete S3 File by key
 * @param { object } action - Action with payload data
 */
export function* deleteS3FileByKey(action) {
  yield put(setLoading(true));
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { key } = data;

  try {
    const response = yield call(s3FileService.deleteS3FileByKey, key);
    yield put(setResponse('success', false, false, { ...response }, callback));
    yield put(setResponse(false, false, false));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        'Error',
        `errors.${err.code}` || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
    yield put(setLoading(false));
  }
  yield put(setLoading(false));
}

/**
 * Declare all component sagas
 */
export function* watchS3FileSaga() {
  yield takeLatest(GET_ALL_S3_FILES, getS3FilesArray);
  yield takeLatest(GET_S3_FILE_BY_KEY, getS3FileByKey);
  yield takeLatest(UPLOAD_S3_FILE, uploadS3File);
  yield takeLatest(DELETE_S3_FILE_BY_KEY, deleteS3FileByKey);
}
